/**
 * Communication Plan hooks. Scoped via the `scope` argument.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export type CommPlanScope = "project";

export interface CommPlanEntry {
  id: number;
  scope_type: CommPlanScope;
  scope_id: string;
  stakeholder: string;
  deliverable: string;
  frequency: string | null;
  priority: string | null;
  owner: string | null;
  delivery_method: string | null;
  comments: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface CommPlanEntryCreate {
  stakeholder: string;
  deliverable: string;
  frequency?: string | null;
  priority?: string | null;
  owner?: string | null;
  delivery_method?: string | null;
  comments?: string | null;
}

export interface CommPlanEntryPatch {
  stakeholder?: string;
  deliverable?: string;
  frequency?: string | null;
  priority?: string | null;
  owner?: string | null;
  delivery_method?: string | null;
  comments?: string | null;
  sort_order?: number;
}

const keys = {
  list: (scope: CommPlanScope, scopeId: string) =>
    ["communication-plan", scope, scopeId] as const,
};

export function useCommunicationPlan(scope: CommPlanScope, scopeId: string | null) {
  return useQuery<CommPlanEntry[]>({
    queryKey: keys.list(scope, scopeId ?? ""),
    queryFn: () =>
      api.get(`/communication-plan/${scope}/${scopeId}`).then((r) => r.data),
    enabled: Boolean(scopeId),
  });
}

export function useCreateCommPlanEntry(scope: CommPlanScope, scopeId: string) {
  const qc = useQueryClient();
  return useMutation<CommPlanEntry, Error, CommPlanEntryCreate>({
    mutationFn: (payload) =>
      api
        .post(`/communication-plan/${scope}/${scopeId}`, payload)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list(scope, scopeId) });
    },
  });
}

export function useUpdateCommPlanEntry(scope: CommPlanScope, scopeId: string) {
  const qc = useQueryClient();
  return useMutation<
    CommPlanEntry,
    Error,
    { id: number; patch: CommPlanEntryPatch }
  >({
    mutationFn: ({ id, patch }) =>
      api.patch(`/communication-plan/id/${id}`, patch).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list(scope, scopeId) });
    },
  });
}

export function useDeleteCommPlanEntry(scope: CommPlanScope, scopeId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      api.delete(`/communication-plan/id/${id}`).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list(scope, scopeId) });
    },
  });
}

/** Frequency presets — match the values surfaced as enum-ish dropdowns
 *  in the UI. Values are stored as raw strings on the backend so users
 *  can override with free-text "Other" if none of these fit. */
export const FREQUENCY_OPTIONS = [
  "Weekly",
  "Bi-weekly",
  "Monthly",
  "Quarterly",
  "Ad-hoc",
] as const;

export const PRIORITY_OPTIONS = ["High", "Medium", "Low"] as const;

export const DELIVERY_METHOD_OPTIONS = [
  "Email",
  "Teams",
  "In-person",
  "Status report",
  "Dashboard",
] as const;

// ---------------------------------------------------------------------------
// Roster — contact-info table that lives next to the plan on the same tab
// ---------------------------------------------------------------------------

export interface RosterEntry {
  id: number;
  scope_type: CommPlanScope;
  scope_id: string;
  name: string;
  project_role: string | null;
  email: string | null;
  phone: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export interface RosterEntryCreate {
  name: string;
  project_role?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface RosterEntryPatch {
  name?: string;
  project_role?: string | null;
  email?: string | null;
  phone?: string | null;
  sort_order?: number;
}

const rosterKeys = {
  list: (scope: CommPlanScope, scopeId: string) =>
    ["communication-roster", scope, scopeId] as const,
};

export function useCommunicationRoster(scope: CommPlanScope, scopeId: string | null) {
  return useQuery<RosterEntry[]>({
    queryKey: rosterKeys.list(scope, scopeId ?? ""),
    queryFn: () =>
      api
        .get(`/communication-plan/${scope}/${scopeId}/roster`)
        .then((r) => r.data),
    enabled: Boolean(scopeId),
  });
}

export function useCreateRosterEntry(scope: CommPlanScope, scopeId: string) {
  const qc = useQueryClient();
  return useMutation<RosterEntry, Error, RosterEntryCreate>({
    mutationFn: (payload) =>
      api
        .post(`/communication-plan/${scope}/${scopeId}/roster`, payload)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rosterKeys.list(scope, scopeId) });
    },
  });
}

export function useUpdateRosterEntry(scope: CommPlanScope, scopeId: string) {
  const qc = useQueryClient();
  return useMutation<RosterEntry, Error, { id: number; patch: RosterEntryPatch }>({
    mutationFn: ({ id, patch }) =>
      api
        .patch(`/communication-plan/roster/${id}`, patch)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rosterKeys.list(scope, scopeId) });
    },
  });
}

export function useDeleteRosterEntry(scope: CommPlanScope, scopeId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (id) =>
      api.delete(`/communication-plan/roster/${id}`).then(() => undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: rosterKeys.list(scope, scopeId) });
    },
  });
}
